import { z } from 'zod';
import {
  householdMemberLimit,
  MAX_HOUSEHOLD_MEMBERS,
  MAX_HOUSEHOLD_NAME_LENGTH,
  type HouseholdRole,
  type PlanId,
  type SubscriptionStatus,
  type SubscriptionStore,
} from './api.js';

/**
 * Contracts for the admin support console.
 *
 * Listing is entirely server-side: search, sort, and paging are all query parameters, and
 * the client owns none of the filtering. Every list URL is hand-editable and shareable, so
 * each field `.catch()`es back to its default rather than rejecting the request — a typo in
 * `?sort=` should degrade to the default ordering, not to a wall of JSON.
 */

export const ADMIN_PAGE_SIZE = 30;
export const ADMIN_PAGE_SIZE_MAX = 100;

export const adminSortDirSchema = z.enum(['asc', 'desc']);
export type AdminSortDir = z.infer<typeof adminSortDirSchema>;

export const adminUserSortSchema = z.enum(['name', 'household', 'lastActive', 'createdAt']);
export type AdminUserSort = z.infer<typeof adminUserSortSchema>;

export const adminHouseholdSortSchema = z.enum(['name', 'members', 'recipes', 'createdAt']);
export type AdminHouseholdSort = z.infer<typeof adminHouseholdSortSchema>;

const pageSchema = z.coerce.number().int().min(1).max(100_000).catch(1);
const pageSizeSchema = z.coerce
  .number()
  .int()
  .min(1)
  .max(ADMIN_PAGE_SIZE_MAX)
  .catch(ADMIN_PAGE_SIZE);
const querySchema = z.string().max(200).catch('');

export const adminUsersQuerySchema = z.object({
  page: pageSchema.default(1),
  pageSize: pageSizeSchema.default(ADMIN_PAGE_SIZE),
  sort: adminUserSortSchema.catch('name').default('name'),
  dir: adminSortDirSchema.catch('asc').default('asc'),
  q: querySchema.default(''),
});
export type AdminUsersQuery = z.infer<typeof adminUsersQuerySchema>;

export const adminHouseholdsQuerySchema = z.object({
  page: pageSchema.default(1),
  pageSize: pageSizeSchema.default(ADMIN_PAGE_SIZE),
  sort: adminHouseholdSortSchema.catch('name').default('name'),
  dir: adminSortDirSchema.catch('asc').default('asc'),
  q: querySchema.default(''),
});
export type AdminHouseholdsQuery = z.infer<typeof adminHouseholdsQuerySchema>;

export interface AdminListResponse<T> {
  rows: T[];
  total: number;
  /** Clamped by the server, so a shrinking result set cannot strand the client past the end. */
  page: number;
  pageSize: number;
}

export interface AdminUserRow {
  id: string;
  name: string;
  email: string;
  /** Every live household the user belongs to. A user may belong to any number of them. */
  households: string[];
  lastLoggedInAt: number | null;
  createdAt: number;
  /** Soft-deleted accounts are listed and marked, never hidden — support still needs them. */
  deleted: boolean;
  /** Carries the `admin` permission. Marked in the list, and never impersonable. */
  admin: boolean;
}

export interface AdminHouseholdRow {
  id: string;
  name: string;
  memberCount: number;
  /** From the projected read model, so it can lag the CRDT for a household behind on sync. */
  recipeCount: number;
  createdAt: number;
  deleted: boolean;
}

export interface AdminOverview {
  environment: string;
  counts: { users: number; households: number };
}

// ---------------------------------------------------------------- impersonation

export const impersonateRequestSchema = z.object({ userId: z.string().min(1).max(200) });
export type ImpersonateRequest = z.infer<typeof impersonateRequestSchema>;

/** What the banner needs to name the way back, present only while impersonating. */
export interface ImpersonationState {
  /** The admin who started it — the account the exit button returns to. */
  adminName: string;
  adminEmail: string;
  startedAt: number;
  /** Impersonation lapses on its own; the banner can say when. */
  expiresAt: number;
}

/**
 * The signed-in identity, as the app should act on it.
 *
 * This exists instead of Better Auth's own `get-session` because that endpoint can only
 * ever answer with the account that authenticated. While an admin is impersonating,
 * `user` here is the *customer* — which is the whole point: every screen below it, and
 * every request it drives, is that customer's.
 */
export interface SessionResponse {
  user: {
    id: string;
    name: string;
    email: string;
    image?: string | null;
    permissions: string[];
  };
  impersonation?: ImpersonationState;
}

// ---------------------------------------------------------------- household detail

/**
 * One membership row, with enough of the person attached to answer "who is in here?"
 * without a second trip to the users list.
 *
 * `loginCount` and `lastLoggedInAt` are the account's own totals across every household
 * they belong to, not their activity in this one — nothing counts sign-ins per household.
 */
export interface AdminHouseholdMember {
  id: string;
  name: string;
  email: string;
  role: HouseholdRole;
  joinedAt: number;
  lastLoggedInAt: number | null;
  loginCount: number;
  /** Soft-deleted accounts stay listed and marked, exactly as they are in the lists. */
  deleted: boolean;
  admin: boolean;
}

/**
 * A subscription row as support needs to read it.
 *
 * `BillingSummary` is the customer's view of the one row that governs their household;
 * this is every row the household holds, including the stale and expired ones, because
 * "I cancelled on my phone and bought on the web" is exactly the question that brings
 * someone to the console.
 */
export interface AdminSubscriptionRow {
  id: string;
  store: SubscriptionStore;
  status: SubscriptionStatus;
  productId: string | null;
  currency: string | null;
  amountMinor: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  /** Display name of whoever bought it; null once that account is gone, or for a comp. */
  startedByName: string | null;
  createdAt: number;
  updatedAt: number;
}

/** The governing entitlement, read straight off `household_entitlement`. */
export interface AdminEntitlement {
  plan: PlanId;
  /** null means unlimited. */
  recipeLimit: number | null;
  source: 'none' | 'revenuecat' | 'complimentary';
  status: 'none' | SubscriptionStatus;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  complimentary: boolean;
  complimentaryReason: string | null;
  pastDueSince: number | null;
  updatedAt: number;
}

/**
 * The most recent comp, from the audit table rather than the entitlement row.
 *
 * `AdminEntitlement.complimentary` says a household is comped; this says who did it. Null
 * for a household comped before the ledger existed — the launch backfill comped every
 * household that already existed, and no agent granted those.
 */
export interface AdminComplimentaryGrant {
  /** Null once the granting account has been hard-deleted. */
  adminName: string | null;
  /** Null for a household created comped from the console without one. */
  reason: string | null;
  grantedAt: number;
}

export interface AdminHouseholdDetail {
  id: string;
  name: string;
  createdAt: number;
  deleted: boolean;
  deletedAt: number | null;
  /** When a soft-deleted household's data is actually destroyed; the restore window. */
  purgeAfter: number | null;
  /** Live recipes, from the projected read model — same caveat as the list's count. */
  recipeCount: number;
  /** In the trash, and still restorable. Counted apart so the two are never confused. */
  deletedRecipeCount: number;
  members: AdminHouseholdMember[];
  entitlement: AdminEntitlement;
  subscriptions: AdminSubscriptionRow[];
  complimentaryGrant: AdminComplimentaryGrant | null;
  /** Invites still waiting on someone, newest per address, exactly as the owner sees them. */
  pendingInvites: AdminPendingInvite[];
}

export interface AdminPendingInvite {
  id: string;
  email: string;
  role: HouseholdRole;
  createdAt: number;
  expiresAt: number;
}

const complimentaryReasonSchema = z.string().trim().min(1).max(300);

/**
 * Comping a household, from the support console.
 *
 * The reason is required because it is not an internal note: it is copied onto the
 * entitlement row, returned in the billing summary, and printed on the customer's own
 * settings screen under "Included at no charge". An agent leaving it blank would publish
 * a blank line to the customer.
 */
export const grantComplimentarySchema = z.object({
  reason: complimentaryReasonSchema,
});
export type GrantComplimentaryRequest = z.infer<typeof grantComplimentarySchema>;

// ---------------------------------------------------------------- invited households

/**
 * Splits whatever an agent pasted into the address box into one address per entry.
 *
 * Commas, semicolons and any whitespace all separate, so a column copied out of a
 * spreadsheet works as well as a line typed by hand. Addresses are lowercased and
 * repeats dropped, because invites are keyed on the lowercased address anyway and two
 * invites to one person would hold two seats in the household.
 */
export function parseEmailList(text: string): string[] {
  const seen = new Set<string>();
  for (const part of text.split(/[\s,;]+/)) {
    const email = part.trim().toLowerCase();
    if (email !== '') seen.add(email);
  }
  return [...seen];
}

/**
 * A new household, comped from the start or left on the free plan, with everyone in
 * `emails` invited to it.
 *
 * Every invitee is invited as an owner. The household is created with no members at all,
 * so whoever accepts first has to be able to run it; an editor-only household would have
 * nobody who could invite, rename or delete. The list is capped at the member limit of the
 * plan the household starts on, since every pending invite holds a seat: a free household
 * has room for one address.
 *
 * Unlike a comp on an existing household, the reason is optional: the agent is setting the
 * household up for people they have already spoken to, so there is often nothing to add. A
 * free household has no comp to give a reason for, so it takes none.
 */
export const createInvitedHouseholdSchema = z
  .object({
    name: z.string().trim().min(1).max(MAX_HOUSEHOLD_NAME_LENGTH),
    emails: z
      .array(z.string().trim().toLowerCase().email())
      .min(1)
      .max(MAX_HOUSEHOLD_MEMBERS)
      .refine((emails) => new Set(emails).size === emails.length, 'Duplicate email address'),
    complimentary: z.boolean(),
    reason: complimentaryReasonSchema.optional(),
  })
  .refine(
    (request) =>
      request.emails.length <= householdMemberLimit(request.complimentary ? 'pro' : 'free'),
    { path: ['emails'], message: 'Too many people for the plan' },
  )
  .refine((request) => request.complimentary || request.reason === undefined, {
    path: ['reason'],
    message: 'Only a comped household takes a reason',
  });
export type CreateInvitedHouseholdRequest = z.infer<typeof createInvitedHouseholdSchema>;

export interface CreateInvitedHouseholdResponse {
  id: string;
}
